/**
 * vue-i18n setup with RTL detection.
 *
 * Languages stored under src/i18n/{lang}.json
 *
 * Active language persisted to localStorage as "c2c_lang".
 * Falls back to navigator.language, then English.
 *
 * RTL languages: ar, fa, he, ur. We set <html dir="rtl"> when these are active.
 */

import { createI18n } from "vue-i18n"
import en from "./i18n/en.json"
import hi from "./i18n/hi.json"
import ar from "./i18n/ar.json"
import fr from "./i18n/fr.json"
import es from "./i18n/es.json"
import de from "./i18n/de.json"
import zh from "./i18n/zh.json"
import pt from "./i18n/pt.json"

export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", native: "English", rtl: false },
  { code: "hi", name: "Hindi", native: "हिन्दी", rtl: false },
  { code: "ar", name: "Arabic", native: "العربية", rtl: true },
  { code: "fr", name: "French", native: "Français", rtl: false },
  { code: "es", name: "Spanish", native: "Español", rtl: false },
  { code: "de", name: "German", native: "Deutsch", rtl: false },
  { code: "zh", name: "Chinese (Simplified)", native: "中文", rtl: false },
  { code: "pt", name: "Portuguese", native: "Português", rtl: false }
] as const

export type LangCode = typeof SUPPORTED_LANGUAGES[number]["code"]

const RTL_LANGS: LangCode[] = ["ar"]

const STORAGE_KEY = "c2c_lang"

function detectInitialLanguage(): LangCode {
  // 1. ?lang= URL parameter takes priority. This makes the hreflang URLs
  //    (https://clip2commons.toolforge.org/?lang=hi, etc.) actually switch
  //    the UI when search engines or shared links land on them.
  try {
    const params = new URLSearchParams(window.location.search)
    const fromUrl = (params.get("lang") || "").toLowerCase() as LangCode
    if (fromUrl && SUPPORTED_LANGUAGES.some(l => l.code === fromUrl)) {
      // Persist so the user keeps the language across reloads.
      localStorage.setItem(STORAGE_KEY, fromUrl)
      return fromUrl
    }
  } catch { /* URL parsing or storage may fail in private mode */ }

  // 2. Last user choice from localStorage.
  const stored = localStorage.getItem(STORAGE_KEY) as LangCode | null
  if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) return stored

  // 3. Browser language.
  const browserLang = (navigator.language || "en").split("-")[0].toLowerCase()
  const match = SUPPORTED_LANGUAGES.find(l => l.code === browserLang)
  return match ? match.code : "en"
}

export const i18n = createI18n({
  legacy: false,
  locale: detectInitialLanguage(),
  fallbackLocale: "en",
  messages: { en, hi, ar, fr, es, de, zh, pt },
  missingWarn: false,
  fallbackWarn: false
})

export function setLanguage(code: LangCode) {
  i18n.global.locale.value = code
  localStorage.setItem(STORAGE_KEY, code)
  applyDirection(code)
  document.documentElement.lang = code
}

export function applyDirection(code: LangCode) {
  const isRTL = RTL_LANGS.includes(code)
  document.documentElement.dir = isRTL ? "rtl" : "ltr"
}

// Apply on load
applyDirection(i18n.global.locale.value as LangCode)
document.documentElement.lang = i18n.global.locale.value
